import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ReturnPolicy = () => {
  const policySections = [
    {
      number: "01",
      title: "No Returns",
      content: "Due to the perishable nature of our products, we do not accept returns. Once an order is placed and shipped, it cannot be returned for health and safety reasons."
    },
    {
      number: "02",
      title: "Order Issues",
      content: "If you receive an incorrect or damaged order, please contact us within 48 hours of delivery. We will review your case and work with you to provide a suitable solution, which may include a replacement or refund."
    },
    {
      number: "03",
      title: "Refunds",
      content: "Refunds will only be issued in the case of: A product that was damaged during shipping, An incorrect item sent (different from what was ordered). If you qualify for a refund, it will be processed within 5-7 business days to your original payment method."
    },
    {
      number: "04",
      title: "Customer Satisfaction",
      content: "Your satisfaction is important to us! If you have any concerns about your order or our products, please reach out to us. We value your feedback and are committed to ensuring you have a positive experience with Bakelore."
    },
    {
      number: "05",
      title: "Changes to Return Policy",
      content: "Bakelore reserves the right to modify this Return Policy at any time. We will notify you of any significant changes by posting a notice on our website. Your continued use of our services constitutes your acceptance of the new policy."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Return Policy | Bakelore</title>
        <meta name="description" content="Bakelore Return Policy - Learn about our return and refund policies for baked goods." />
        <meta name="keywords" content="Bakelore, Return Policy, Refund Policy, Returns and Refunds" />
        <meta property="og:title" content="Return Policy | Bakelore" />
        <meta property="og:description" content="Learn about our return and refund policies for baked goods." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Return Policy | Bakelore" />
        <meta name="twitter:description" content="Learn about our return and refund policies for baked goods." />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1509440159596-0249088772ff?w=1920&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#C08B89]/90 via-[#C08B89]/70 to-[#C08B89]/90" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="inline-block mb-4 px-4 py-1 bg-white/20 rounded-full backdrop-blur-sm">
            <span className="text-white text-sm font-medium tracking-wider uppercase">
              Policy Information
            </span>
          </div>
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: "Philosopher, serif" }}
          >
            Return Policy
          </h1>
          <p 
            className="text-white/90 text-lg md:text-xl max-w-2xl mb-8"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Quality & Freshness Guaranteed
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-white/90 transition-all duration-300 shadow-lg">
                Contact Us
              </button>
            </Link>
            <Link to="/">
              <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Back to Home
              </button>
            </Link>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#faf8f5"/>
          </svg>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-gray-100">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-[#C08B89]/10 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C08B89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p 
              className="text-gray-700 text-lg md:text-xl text-center leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              At <span className="font-bold text-[#C08B89]">Bakelore</span>, we take great pride in the quality and freshness of our baked goods. Due to the perishable nature of our products, we have specific policies regarding returns and refunds. Please read our policy carefully.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid gap-6">
            {policySections.map((section, index) => (
              <div 
                key={index}
                className="group bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#C08B89]/30"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C08B89] to-[#d4a5a3] text-white font-bold text-lg rounded-lg">
                      {section.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 
                      className="text-xl md:text-2xl font-bold text-gray-800 mb-3 group-hover:text-[#C08B89] transition-colors"
                      style={{ fontFamily: "Philosopher, serif" }}
                    >
                      {section.title}
                    </h2>
                    <p 
                      className="text-gray-600 leading-relaxed"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#C08B89] to-[#d4a5a3] rounded-2xl shadow-xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <h2 
                className="kint text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "Philosopher, serif" }}
              >
                Have Questions?
              </h2>
              <p className="text-white/90 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                If you have any questions or concerns regarding our Return Policy, please contact us.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Address</h3>
                <p className="text-white/80 text-sm">
                  1-103/5/1 Hitec City, Main Road, Madhapur, Hyderabad, Telangana, 500081.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Email</h3>
<a href="mailto:hello@bakelore.com" className="text-white/80 hover:text-white hover:underline">
                  hello@bakelore.com
                </a>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Call/WhatsApp</h3>
                <div className="flex flex-col gap-1">
                  <a href="tel:+918121034991" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034991</a>
                  <a href="tel:+918121034992" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034992</a>
                  <a href="tel:+918121034993" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034993</a>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-12 bg-[#faf8f5]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p 
            className="text-gray-700 text-xl"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Thank you for choosing <span className="font-bold text-[#C08B89]">Bakelore</span>! We appreciate your understanding and support.
          </p>
        </div>
      </section>
    </>
  );
};

export default ReturnPolicy;

